import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { Card, CardContent } from "./ui/card";
import { SEOHead } from "./SEOHead";
import { useTranslations } from "../hooks/useTranslations";
import { CREATORS_LANDING_PATH, getCreatorsLandingLabel } from "../content/creatorsLanding";
import { getCreatorsArticleContent, type CreatorsArticleBlock } from "../content/creatorsArticle";
import { getLocalizedPath } from "../utils/localizedRoutes";

const CREATORS_HERO_IMAGE = "/creators-images-best-ai-voice-generator-for-creators-cropped.png";

const backToHomeByLanguage: Record<string, string> = {
  en: "Back to home",
  ru: "На главную",
  es: "Volver al inicio",
  fr: "Retour à l'accueil",
  de: "Zurück zur Startseite",
  it: "Torna alla home",
  pt: "Voltar à página inicial",
  pl: "Wróć do strony głównej",
  ja: "ホームに戻る",
  ko: "홈으로 돌아가기",
  zh: "返回首页",
  ar: "العودة إلى الرئيسية",
  bg: "Обратно към началната страница",
  hr: "Povratak na početnu",
  cs: "Zpět na hlavní stránku",
  da: "Tilbage til forsiden",
  nl: "Terug naar home",
  et: "Tagasi avalehele",
  fi: "Takaisin etusivulle",
  el: "Επιστροφή στην αρχική",
  hu: "Vissza a főoldalra",
  ga: "Ar ais go dtí an baile",
  lv: "Atpakaļ uz sākumlapu",
  lt: "Grįžti į pradžios puslapį",
  mt: "Lura għall-paġna ewlenija",
  ro: "Înapoi la pagina principală",
  sk: "Späť na hlavnú stránku",
  sl: "Nazaj na domačo stran",
  sv: "Tillbaka till startsidan",
};

function renderCreatorsArticleBlock(block: CreatorsArticleBlock, index: number) {
  switch (block.type) {
    case "heading":
      if (block.level === 2) {
        return (
          <h2 key={index} className="mt-6 mb-4 text-2xl font-semibold text-gray-900">
            {block.text}
          </h2>
        );
      }

      return (
        <h3 key={index} className="mt-5 mb-3 text-xl font-semibold text-gray-900">
          {block.text}
        </h3>
      );

    case "paragraph":
      return (
        <p key={index} className="text-gray-700 leading-relaxed">
          {block.text}
        </p>
      );

    case "list":
      return (
        <ul key={index} className="list-disc space-y-2 pl-6 text-gray-700 leading-relaxed">
          {block.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      );

    case "checklist":
      return (
        <div key={index} className="grid gap-3 md:grid-cols-2">
          {block.items.map((item) => (
            <div
              key={item}
              className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-gray-700"
            >
              <span className="font-medium text-gray-900">✓</span> {item}
            </div>
          ))}
        </div>
      );

    case "callout":
      return (
        <div
          key={index}
          className="rounded-lg border border-gray-200 bg-gray-50 px-5 py-4 text-lg font-semibold leading-relaxed text-gray-900"
        >
          {block.text}
        </div>
      );
  }
}

export function CreatorsPage() {
  const { currentLanguage } = useTranslations();
  const article = getCreatorsArticleContent(currentLanguage);
  const homeUrl = getLocalizedPath("/", currentLanguage);
  const creatorsUrl = getLocalizedPath(CREATORS_LANDING_PATH, currentLanguage);
  const creatorsLabel = getCreatorsLandingLabel(currentLanguage);
  const backToHomeLabel = backToHomeByLanguage[currentLanguage] || backToHomeByLanguage.en;

  return (
    <>
      <SEOHead
        overrideSeo={{
          title: article.seoTitle,
          description: article.seoDescription,
          keywords: article.keywords,
        }}
        canonicalPathOverride={creatorsUrl}
        canonicalPathIsLocalized={true}
      />
      <Header />
      <div className="min-h-screen vevi-site-background" style={{ paddingTop: "112px" }}>
        <div className="vevi-page-grid">
          <div className="vevi-page-main py-6 sm:py-8 lg:py-10" style={{ paddingBottom: "72px" }}>
            <div className="mb-6 overflow-hidden rounded-xl border border-white/15 bg-slate-950/20 shadow-xl">
              <div className="relative">
                <img
                  src={CREATORS_HERO_IMAGE}
                  alt={article.h1}
                  className="block h-auto w-full"
                />
              </div>
            </div>

            <div className="mb-6">
              <div className="mb-4 flex flex-wrap items-center gap-3 text-sm font-medium text-white">
                <Link
                  to={homeUrl}
                  className="inline-flex items-center transition-colors hover:text-purple-100"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  <span>{backToHomeLabel}</span>
                </Link>
                <span className="text-purple-200/80">|</span>
                <Link
                  to={creatorsUrl}
                  className="transition-colors hover:text-purple-100"
                >
                  {creatorsLabel}
                </Link>
              </div>

              <h1
                className="mb-4 max-w-5xl font-semibold tracking-tight text-white"
                style={{ fontSize: "48px", lineHeight: "0.98" }}
              >
                {article.h1}
              </h1>

              <p className="max-w-4xl text-base leading-relaxed text-purple-100 sm:text-lg">
                {article.heroDescription}
              </p>
            </div>

            <Card className="bg-white/95 backdrop-blur-sm shadow-xl">
              <CardContent className="p-8">
                <div className="space-y-4">
                  {article.blocks.map((block, index) => renderCreatorsArticleBlock(block, index))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}
